import axios from 'axios';
import { http, httpApi } from './http';
import type { 
  Milestone, 
  MilestoneApiResponse, 
  MilestonePaginationParams, 
  CreateMilestoneData, 
  UpdateMilestoneData,
  BenefitData 
} from '../types/milestone';

// Export empty object to make this a module
export {};

const api = http;

export class MilestoneService {
  // Get all milestones with pagination and filtering
  static async getMilestones(params: MilestonePaginationParams = {}): Promise<MilestoneApiResponse> {
    try {
      const response = await api.post<MilestoneApiResponse>('/api/milestones', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching milestones:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch milestones');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Search milestones
  static async searchMilestones(searchParams: MilestonePaginationParams): Promise<MilestoneApiResponse> {
    try {
      const response = await api.post<MilestoneApiResponse>('/api/milestones', searchParams);
      return response.data;
    } catch (error) {
      console.error('Error searching milestones:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to search milestones');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get milestone by ID
  static async getMilestoneById(id: string): Promise<Milestone> {
    try {
      const response = await api.get<{ success: boolean; data: Milestone }>(`/api/milestones/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Create milestone
  static async createMilestone(milestoneData: CreateMilestoneData): Promise<Milestone> {
    try {
      const response = await api.post<{ success: boolean; data: Milestone }>('/api/milestones/create', milestoneData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to create milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Update milestone
  static async updateMilestone(id: string, milestoneData: UpdateMilestoneData): Promise<Milestone> {
    try {
      const response = await api.put<{ success: boolean; data: Milestone }>(`/api/milestones/${id}`, milestoneData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to update milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Delete milestone
  static async deleteMilestone(id: string): Promise<{ success: boolean }> {
    try {
      const response = await api.delete<{ success: boolean }>(`/api/milestones/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get milestones by card
  static async getMilestonesByCard(cardId: string, filters?: Partial<MilestonePaginationParams>): Promise<MilestoneApiResponse> {
    try {
      const response = await api.post<MilestoneApiResponse>(`/api/milestones/card/${cardId}`, filters || {});
      return response.data;
    } catch (error) {
      console.error('Error fetching milestones by card:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch milestones by card');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get milestones by bank
  static async getMilestonesByBank(bankSlug: string, filters?: Partial<MilestonePaginationParams>): Promise<MilestoneApiResponse> {
    try {
      const response = await api.post<MilestoneApiResponse>(`/api/milestones/bank/${bankSlug}`, filters || {});
      return response.data;
    } catch (error) {
      console.error('Error fetching milestones by bank:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch milestones by bank');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Add benefit to milestone
  static async addBenefitToMilestone(milestoneId: string, benefitData: BenefitData): Promise<Milestone> {
    try {
      const response = await api.post<{ success: boolean; data: Milestone }>(`/api/milestones/${milestoneId}/benefits`, benefitData);
      return response.data.data;
    } catch (error) {
      console.error('Error adding benefit to milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to add benefit to milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Update benefit in milestone
  static async updateBenefitInMilestone(milestoneId: string, benefitIndex: number, benefitData: BenefitData): Promise<Milestone> {
    try {
      const response = await api.put<{ success: boolean; data: Milestone }>(`/api/milestones/${milestoneId}/benefits/${benefitIndex}`, benefitData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating benefit in milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to update benefit in milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Delete benefit from milestone
  static async deleteBenefitFromMilestone(milestoneId: string, benefitIndex: number): Promise<Milestone> {
    try {
      const response = await api.delete<{ success: boolean; data: Milestone }>(`/api/milestones/${milestoneId}/benefits/${benefitIndex}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting benefit from milestone:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete benefit from milestone');
      }
      throw new Error('An unexpected error occurred');
    }
  }
}
